import { FC, ReactNode, useCallback, useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import { BackButton } from './PunchupShowDashboardPage.styles';
import { IdParamsType } from 'utils/types';
import { isEmbeddedInIframe } from 'utils/utils';
import { MainLayout } from 'layouts/MainLayout';
import { ROUTES } from 'features/routes';

interface PunchupShowDashboardPageProps {
	children: ReactNode;
	pageName: string;
	onBack?: () => void;
}

export const PunchupShowDashboardPage: FC<PunchupShowDashboardPageProps> = ({ children, pageName, onBack }) => {
	const { push } = useHistory();
	const { id } = useParams<IdParamsType>();

	const handleBack = useCallback(() => {
		if (onBack) {
			onBack();
		} else {
			push(generatePath(ROUTES.DASHBOARD_PATH, { id }));
		}
	}, [onBack, push, id]);

	useEffect(() => {
		window.parent.postMessage({ action: 'sub-page-enter', pageName }, '*');
		window.parent.postMessage({ action: 'scroll-to-top' }, '*');
		return () => {
			window.parent.postMessage({ action: 'sub-page-exit' }, '*');
		};
	}, [pageName]);

	useEffect(() => {
		const handleParentMessage = (event: MessageEvent) => {
			if (!isEmbeddedInIframe()) return;
			if (event.source !== window.parent) return;
			if (event.data?.action === 'navigate-to-dashboard') {
				handleBack();
			}
		};

		window.addEventListener('message', handleParentMessage);
		return () => window.removeEventListener('message', handleParentMessage);
	}, [handleBack]);

	// In an iframe (Punchup) the breadcrumb owns back navigation — no button needed.
	// Standalone tixologist has no breadcrumb, so we render one here.
	const isEmbedded = isEmbeddedInIframe();

	return (
		<MainLayout>
			{!isEmbedded && <BackButton onClick={handleBack}>← Back</BackButton>}
			{children}
		</MainLayout>
	);
};
